const hiddenClass = "selling_plan_theme_integration--hidden";

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.updatePurchasingFrequencyLabels();
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.initialSellingPlanCheck();
    this.listenToRadioButtonChange();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute("data-section-id");
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    return (
      this.addToCartForms[1]?.querySelector(`input[name="id"]`) ||
      this.addToCartForms[1]?.querySelector(`select[name="id"]`) ||
      this.addToCartForms[0].querySelector(`input[name="id"]`) ||
      this.addToCartForms[0].querySelector(`select[name="id"]`)
    );
  }

  get visibleSellingPlanForm() {
    return this.shopifySection.querySelector(
      `section[data-variant-id^="${this.variantIdInput.value}"]`
    );
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector(".selected-selling-plan-id");
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('.product-info__buy-buttons [action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.initialSellingPlanCheck();
    this.listenToRadioButtonChange();
    this.updateSellingPlanInputsValues();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput.tagName === "INPUT") {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange(mutationRecord.target.value);
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener("change", () => {
        this.handleVariantChange();
      });
    });
  }

  updatePurchasingFrequencyLabels() {
    const labels = this.sellingPlansWidgetContainer.querySelectorAll(
      ".product-purchasing-allocations label span"
    );
    labels.forEach((label) => {
      const text = label.innerText;
      const match = text.match(/\d+/); // Extracts the first number found
      if (match) {
        label.innerText = match[0]; // Updates the label with just the number
      }
    });
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll(".selected-selling-plan-id");
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    return this._selectedPurchaseOption;
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
  }

  initialSellingPlanCheck() {
    const purchasingOptions = this.visibleSellingPlanForm?.querySelectorAll(
      ".product-purchasing-option"
    );

    if (!purchasingOptions) {
      return;
    }

    purchasingOptions.forEach((option) => {
      if (!option.classList.contains("active")) {
        return;
      }
      const parentInput = option.querySelector("input.purchase-option-parent");
      const purchasingFrequency = option.querySelector(
        ".product-purchasing-frequency"
      );

      if (purchasingFrequency) {
        const selectedOption = purchasingFrequency.querySelector(
          '.popover-listbox__option[aria-selected="true"]'
        );
        this.handleRadioButtonChange(selectedOption);
      } else if (parentInput) {
        this.handleRadioButtonChange(parentInput);
      }
    });
  }

  listenToRadioButtonChange() {
    const form = this.visibleSellingPlanForm;
    if (!form || form._listenersBound) {
      return;
    }
    form._listenersBound = true;
    const purchasingOptions = form.querySelectorAll(
      ".product-purchasing-option"
    );
    const radioButtons = form.querySelectorAll('input[type="radio"]');
    const popoverWrapper = form.querySelector(".product-purchasing-frequency");
    const popover = form.querySelector(".popover-listbox");

    radioButtons.forEach((radio) => {
      radio.addEventListener("change", (event) => {
        if (radio.classList.contains("purchase-option-parent")) {
          purchasingOptions.forEach((option) => {
            option.classList.remove("active");
          });
          const radioParent = radio.closest(".product-purchasing-option");
          radioParent.classList.add("active");
          const purchasingFrequency = radioParent.querySelector(
            ".product-purchasing-frequency"
          );
          if (event.isTrusted) {
            if (purchasingFrequency) {
              const selectedOption = popover.querySelector(
                '.popover-listbox__option[aria-selected="true"]'
              );
              this.handleRadioButtonChange(selectedOption);
            } else {
              this.handleRadioButtonChange(event.target);
            }
          }
        }
      });
    });

    if (popover) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "aria-selected"
          ) {
            // Find the button that is now selected
            const selectedOption = popover.querySelector(
              '.popover-listbox__option[aria-selected="true"]'
            );
            if (!selectedOption) {
              return;
            }

            this.handleRadioButtonChange(selectedOption);

            const parentInput = popoverWrapper
              .closest(".product-purchasing-option")
              ?.querySelector("input.purchase-option-parent");
            if (parentInput && !parentInput.checked) {
              parentInput.checked = true;
              parentInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
            return selectedOption;
          }
        }
      });

      // Start observing child buttons for aria-selected changes
      observer.observe(popover, {
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-selected"],
      });
    }
  }
}

document
  .querySelectorAll(".selling_plan_app_container")
  .forEach((sellingPlansWidgetContainer) => {
    window.sellingPlanWidgets = window.sellingPlanWidgets || [];
    window.sellingPlanWidgets.push(new SellingPlansWidget(sellingPlansWidgetContainer));
  });
